/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Spin, Table, Tag, Tooltip, Typography } from 'antd'
import clsx from 'clsx'
import { ANNOTATION_CATEGORIES } from 'constants/annotation'
import dayjs from 'dayjs'
import {
  CommentDomainService,
  CommentEntity,
  CommentFormService,
  CommentItem,
  decodeHtmlEntities,
  ObjectCategory,
  useFetchReplies
} from 'domain/comment'
import { UserEntity } from 'domain/user/UserEntity'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { CurrentUser } from 'models/User'
import QuillEditorV3, {
  isQuillEmpty,
  QuillEditorV3Ref
} from 'pages/building/panoramas/components/ModalAlertSetting/QuillEditorV3'
import useSWR from 'swr'
import {
  fetchCommentBodyContent,
  isS3Path,
  isS3Url,
  uploadCommentBodyToS3
} from 'utils/commentS3Utils'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

export const CATEGORIES = ANNOTATION_CATEGORIES

type Props = {
  open: boolean
  comment: CommentItem
  userList?: any[]
  userGroups?: any[]
  getOrgString?: (u: any) => string
  getRoleString?: (u: any) => string
  onCancel: () => void
  onSave: (payload: any) => void
  onReply?: (
    commentId: number,
    payload: string | { body: string; mentions?: any[] }
  ) => Promise<void>
  onUpdateReply?: (
    replyId: number,
    payload: { body: string; mentions?: any[] }
  ) => Promise<void>
  onDeleteReply?: (replyId: number) => Promise<void>
  onEdit?: () => void
  onDelete?: (commentId: number) => void
  onRefresh?: () => Promise<void> | void
  isSubmitting?: boolean
}

function ResolvedReplyBody({
  reply,
  formatBodyForDisplay
}: {
  reply: any
  formatBodyForDisplay: (text: string) => string
}) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  )
  const [content, setContent] = useState('')

  const targetPath =
    reply.s3_path ||
    reply.s3Path ||
    (!isS3Path(reply.body) ? reply.body : '') ||
    ''

  useEffect(() => {
    let isMounted = true
    if (!targetPath) {
      setStatus('success')
      setContent(reply.body && !isS3Path(reply.body) ? reply.body : '')
      return
    }
    if (!content) {
      setStatus('loading')
    }
    fetchCommentBodyContent(targetPath)
      .then((text) => {
        if (isMounted) {
          if (isS3Url(text) || isS3Path(text)) {
            setStatus('error')
          } else {
            setContent(text || '')
            setStatus('success')
          }
        }
      })
      .catch(() => {
        if (isMounted && !content) {
          setStatus('error')
        }
      })
    return () => {
      isMounted = false
    }
  }, [reply.id, reply.body, targetPath])

  if (status === 'loading') {
    return (
      <div className="py-1 text-[11px] text-slate-400 flex items-center gap-1.5">
        <Spin size="small" />
        <span>返信を読み込み中...</span>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <Tag color="error" className="text-[11px] my-0.5">
        ● 返信の取得に失敗しました
      </Tag>
    )
  }

  return (
    <div
      className="ql-editor comment-body-display whitespace-pre-wrap text-slate-800 break-words !p-0 !min-h-0 !h-auto !overflow-visible"
      dangerouslySetInnerHTML={{
        __html: formatBodyForDisplay(content || '')
      }}
    />
  )
}

function getInitials(name: string): string {
  if (!name) return '?'
  const trimmed = name.trim()
  if (!trimmed) return '?'
  return trimmed.slice(0, 1).toUpperCase()
}

export default function ModalCommentDetailRN({
  open,
  comment,
  userList = [],
  userGroups = [],
  getOrgString = () => '-',
  getRoleString = () => '-',
  onCancel,
  onSave,
  onReply = async () => {},
  onUpdateReply,
  onDeleteReply,
  onEdit = undefined,
  onDelete = undefined,
  onRefresh = undefined,
  isSubmitting = false
}: Props) {
  const { t } = useTranslation()
  const { data: profile } = useSWR<CurrentUser | null>('v1/users/me')
  const { getDirectoryPrimaryColor } = useDirectory()
  const { trigger: getPresignedUrl } = useMutation(
    'v1/annotations/presigned-urls'
  )

  useEffect(() => {
    if (open) {
      const targetId = comment?.annotationId || comment?.id
      if (targetId) {
        CommentDomainService.panKrpanoToCommentMarker(targetId)
      }
    }
  }, [open, comment?.id, comment?.annotationId])

  const [objectCat, setObjectCat] = useState<ObjectCategory>('A')
  const [status, setStatus] = useState<'open' | 'resolved'>('open')
  const [recipients, setRecipients] = useState<number[]>([])
  const [replyText, setReplyText] = useState('')
  const [showRecipients, setShowRecipients] = useState(false)

  const quillEditorRef = useRef<QuillEditorV3Ref>(null)
  const editorWrapperRef = useRef<HTMLDivElement>(null)
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false)
  const [showMentionMenu, setShowMentionMenu] = useState(false)
  const [mentionSearchQuery, setMentionSearchQuery] = useState('')
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [mentionPos, setMentionPos] = useState<{
    left: number
    top: number
  } | null>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMentionMenu &&
        editorWrapperRef.current &&
        !editorWrapperRef.current.contains(event.target as Node)
      ) {
        setShowMentionMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMentionMenu])

  const insertMentionTag = useCallback(
    (type: string, name: string, targetId?: number | string) => {
      if (quillEditorRef.current) {
        quillEditorRef.current.insertMention(type, name, targetId)
      } else {
        const tagHtml = CommentFormService.buildMentionTagHtml(
          type,
          name,
          targetId
        )
        setReplyText((prev) => prev + tagHtml)
      }
      setShowMentionMenu(false)
      setMentionSearchQuery('')
    },
    []
  )

  useEffect(() => {
    setMentionSelectedIndex(0)
  }, [mentionSearchQuery, showMentionMenu])

  const mentionItems = useMemo(() => {
    const { showAll, filteredGroups, filteredUsers } =
      CommentFormService.filterMentions(
        mentionSearchQuery,
        userGroups,
        userList
      )

    const items: Array<{
      id: string
      type: string
      label: string
      action: () => void
    }> = []

    if (showAll) {
      items.push({
        id: 'all',
        type: 'all',
        label: 'all （全体通知）',
        action: () => insertMentionTag('all', 'all')
      })
    }

    filteredGroups.forEach((g: any) => {
      const gName = g.group_name || g.groupName || 'グループ'
      items.push({
        id: `group-${g.id}`,
        type: 'group',
        label: `${gName} （グループ）`,
        action: () => insertMentionTag('group', gName, g.id)
      })
    })

    filteredUsers.forEach((u: any) => {
      const uName =
        `${u.last_name || u.lastName || ''} ${
          u.first_name || u.firstName || ''
        }`.trim() ||
        u.name ||
        'ユーザー'
      items.push({
        id: `user-${u.id || u.user_id}`,
        type: 'user',
        label: uName,
        action: () => insertMentionTag('user', uName, u.id || u.user_id)
      })
    })

    return items
  }, [mentionSearchQuery, userGroups, userList, insertMentionTag])

  useEffect(() => {
    if (showMentionMenu && itemRefs.current[mentionSelectedIndex]) {
      itemRefs.current[mentionSelectedIndex]?.scrollIntoView({
        block: 'nearest'
      })
    }
  }, [mentionSelectedIndex, showMentionMenu])

  const handleQuillKeyDown = useCallback(
    (e: KeyboardEvent): boolean => {
      if (!showMentionMenu) return false

      if (e.key === 'Escape') {
        setShowMentionMenu(false)
        return true
      }

      if (mentionItems.length === 0) return false

      if (e.key === 'ArrowDown') {
        setMentionSelectedIndex((prev) => (prev + 1) % mentionItems.length)
        return true
      }

      if (e.key === 'ArrowUp') {
        setMentionSelectedIndex(
          (prev) => (prev - 1 + mentionItems.length) % mentionItems.length
        )
        return true
      }

      if (
        (e.key === 'Enter' || e.key === 'Tab') &&
        !e.isComposing &&
        e.keyCode !== 229
      ) {
        const selectedItem = mentionItems[mentionSelectedIndex]
        if (selectedItem) {
          selectedItem.action()
          return true
        }
      }

      return false
    },
    [showMentionMenu, mentionItems, mentionSelectedIndex]
  )
  const [editingReplyId, setEditingReplyId] = useState<number | null>(null)
  const [deleteReplyConfirmOpen, setDeleteReplyConfirmOpen] = useState(false)
  const [pendingDeleteReplyId, setPendingDeleteReplyId] = useState<
    number | null
  >(null)
  const [resolvedCommentBody, setResolvedCommentBody] = useState('')
  const [commentBodyStatus, setCommentBodyStatus] = useState<
    'loading' | 'success' | 'error'
  >('loading')

  const sanitizeCommentHtml = (dirtyHtml: string): string => {
    if (!dirtyHtml || typeof dirtyHtml !== 'string') return ''
    return dirtyHtml
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
      .replace(/<object[\s\S]*?>[\s\S]*?<\/object>/gi, '')
      .replace(/<embed[\s\S]*?>[\s\S]*?<\/embed>/gi, '')
      .replace(/<form[\s\S]*?>[\s\S]*?<\/form>/gi, '')
      .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/(href|src)\s*=\s*["']?\s*javascript:[^"'>\s]*/gi, '')
  }

  const formatBodyForDisplay = (text: string) => {
    if (!text) return ''
    const sanitized = sanitizeCommentHtml(text)
    if (/<[a-z][\s\S]*>/i.test(sanitized)) {
      return sanitized
    }
    return sanitized.replace(/@([^\s@]+)/g, (match, name) => {
      if (name.toLowerCase() === 'all') {
        return `<span class="text-amber-600 font-semibold">${match}</span>`
      }
      return `<span class="text-sky-600 font-semibold">${match}</span>`
    })
  }

  const commentId =
    comment?.id ||
    (comment as any)?.annotation_id ||
    (comment as any)?.annotationId

  const {
    replies: fetchedReplies,
    hasMoreOlder,
    isLoading,
    isLoadingMore,
    loadMoreOlder,
    refetchLoadedPages
  } = useFetchReplies(commentId, open)

  const activeReplies = useMemo(() => {
    const list = [...(fetchedReplies || [])]
    const seen = new Set(list.map((r: any) => String(r.id)))
    if (Array.isArray(comment?.replies)) {
      comment.replies.forEach((r: any) => {
        if (r && r.id && !seen.has(String(r.id))) {
          seen.add(String(r.id))
          list.unshift(r)
        }
      })
    }
    return list
  }, [fetchedReplies, comment?.replies])

  const repliesContainerRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (repliesContainerRef.current) {
        repliesContainerRef.current.scrollTop =
          repliesContainerRef.current.scrollHeight
      }
    })
  }, [])

  const isInitialScrollDone = useRef(false)

  useEffect(() => {
    if (!open) {
      isInitialScrollDone.current = false
    }
  }, [open])

  useEffect(() => {
    if (
      open &&
      activeReplies &&
      activeReplies.length > 0 &&
      !isInitialScrollDone.current
    ) {
      isInitialScrollDone.current = true
      scrollToBottom()
    }
  }, [open, activeReplies, scrollToBottom])

  const handleContainerScroll = useCallback(
    async (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      if (target.scrollTop <= 15 && hasMoreOlder && !isLoadingMore) {
        const prevScrollHeight = target.scrollHeight
        await loadMoreOlder()
        requestAnimationFrame(() => {
          if (target) {
            const newScrollHeight = target.scrollHeight
            target.scrollTop = newScrollHeight - prevScrollHeight
          }
        })
      }
    },
    [hasMoreOlder, isLoadingMore, loadMoreOlder]
  )

  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    if (isRefreshing || isLoading || isLoadingMore) return
    setIsRefreshing(true)
    try {
      const promises: Promise<any>[] = [refetchLoadedPages()]
      if (onRefresh) {
        promises.push(Promise.resolve(onRefresh()))
      }
      const targetPath =
        (comment as any)?.s3_path || comment?.s3Path || comment?.body || ''
      if (targetPath) {
        promises.push(
          fetchCommentBodyContent(targetPath)
            .then((text) => {
              if (!isS3Url(text) && !isS3Path(text)) {
                setResolvedCommentBody(text || '')
                setCommentBodyStatus('success')
              }
            })
            .catch(() => {})
        )
      }
      await Promise.all(promises)
      scrollToBottom()
      setTimeout(scrollToBottom, 60)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleSendReply = async () => {
    if (isSubmitting || isLocalSubmitting) return
    const currentReplyHtml = replyText
    if (isQuillEmpty(currentReplyHtml) || !commentId) return

    setIsLocalSubmitting(true)
    try {
      const { s3Path, s3Url } = await uploadCommentBodyToS3(
        currentReplyHtml,
        getPresignedUrl
      )
      const mentions =
        CommentFormService.extractMentionsFromHtml(currentReplyHtml)

      if (editingReplyId) {
        if (onUpdateReply) {
          await onUpdateReply(editingReplyId, {
            body: s3Url,
            s3_path: s3Path,
            s3Path,
            mentions
          } as any)
        }
        setEditingReplyId(null)
      } else {
        await onReply(commentId, {
          body: s3Url,
          s3_path: s3Path,
          s3Path,
          mentions
        } as any)
      }

      setReplyText('')
      quillEditorRef.current?.clear()
      await refetchLoadedPages()
      scrollToBottom()
    } catch {
      // ignore
    } finally {
      setIsLocalSubmitting(false)
    }
  }

  const handleStartEditReply = (r: any) => {
    setEditingReplyId(r.id)
    const targetPath =
      [r.s3_path, r.s3Path, r.body].find((p) => p && isS3Url(p)) ||
      r.s3_path ||
      r.s3Path ||
      r.body ||
      ''

    const setContentAndFocusEnd = (content: string) => {
      setReplyText(content)
      quillEditorRef.current?.setContent(content)
      setTimeout(() => {
        quillEditorRef.current?.focusEnd()
      }, 50)
    }

    if (!isS3Url(targetPath) && !targetPath.startsWith('assets/')) {
      setContentAndFocusEnd(targetPath)
    } else {
      setReplyText('')
      quillEditorRef.current?.clear()
    }
    fetchCommentBodyContent(targetPath)
      .then((text) => {
        if (!isS3Url(text) && !text.startsWith('assets/')) {
          setContentAndFocusEnd(text || '')
        }
      })
      .catch(() => {
        const fallback = [r.body, r.s3Path, r.s3_path].find(
          (b) => b && !isS3Url(b) && !b.startsWith('assets/')
        )
        if (fallback) {
          setContentAndFocusEnd(fallback)
        }
      })
  }

  const handleCancelEditReply = () => {
    setEditingReplyId(null)
    setReplyText('')
    quillEditorRef.current?.clear()
  }

  const handleConfirmDeleteReply = async () => {
    if (!pendingDeleteReplyId) return
    if (onDeleteReply) {
      await onDeleteReply(pendingDeleteReplyId)
    }
    if (editingReplyId === pendingDeleteReplyId) {
      handleCancelEditReply()
    }
    setDeleteReplyConfirmOpen(false)
    setPendingDeleteReplyId(null)
    await refetchLoadedPages()
  }

  const prevCommentIdRef = useRef<number | string | null>(null)

  useEffect(() => {
    let isMounted = true
    const currentCommentId = comment?.id ?? null
    const isCommentChanged = prevCommentIdRef.current !== currentCommentId

    if (open && comment) {
      if (isCommentChanged) {
        prevCommentIdRef.current = currentCommentId
        setObjectCat(comment?.objectCat || 'A')
        setStatus(comment?.status || 'open')
        setReplyText('')
        setShowRecipients(false)
        setEditingReplyId(null)
      }

      const targetPath =
        (comment as any)?.s3_path || comment?.s3Path || comment?.body || ''
      if (!targetPath) {
        setCommentBodyStatus('success')
        setResolvedCommentBody('')
      } else if (isCommentChanged || commentBodyStatus === 'loading') {
        setCommentBodyStatus('loading')
        fetchCommentBodyContent(targetPath)
          .then((content) => {
            if (isMounted) {
              if (isS3Url(content) || isS3Path(content)) {
                setCommentBodyStatus('error')
              } else {
                setResolvedCommentBody(content || '')
                setCommentBodyStatus('success')
              }
            }
          })
          .catch(() => {
            if (isMounted) {
              setCommentBodyStatus('error')
            }
          })
      }

      if (comment?.recipients && comment.recipients.length > 0) {
        const parsed = CommentFormService.parseRecipientIds(comment.recipients)
        const valid = CommentDomainService.filterValidRecipients(
          parsed,
          userList
        )
        setRecipients(valid)
      } else if (isCommentChanged) {
        setRecipients(CommentFormService.getAdminRecipientIds(userList))
      }
    } else if (!open) {
      prevCommentIdRef.current = null
    }
    return () => {
      isMounted = false
    }
  }, [open, comment, userList])

  const recipientDisplay = CommentEntity.from({
    recipients
  }).getRecipientDisplayInfo(userList)

  const handleSaveStatus = () => {
    onSave({
      id: comment.id,
      title: comment.title,
      objectCat,
      scope: comment.scope,
      status,
      body: comment.body,
      recipients
    })
  }

  const getAuthorNameFromUser = (
    authorObj: any,
    authorId?: number | string
  ) => {
    if (authorObj?.name) return authorObj.name
    const lName = authorObj?.lastName || authorObj?.last_name || ''
    const fName = authorObj?.firstName || authorObj?.first_name || ''
    const full = `${lName} ${fName}`.trim()
    if (full) return full

    const uId = authorId || authorObj?.id
    if (uId && Array.isArray(userList)) {
      const found = userList.find((u: any) => String(u.id) === String(uId))
      if (found) {
        const uFull = `${found.lastName || found.last_name || ''} ${
          found.firstName || found.first_name || ''
        }`.trim()
        if (uFull) return uFull
        if (found.name) return found.name
      }
    }

    return authorObj?.email || (uId ? `ユーザー #${uId}` : '投稿者')
  }

  const findUserById = (userId?: number | string) => {
    if (!userId || !Array.isArray(userList)) return null
    return userList.find((u: any) => String(u.id) === String(userId)) || null
  }

  const authorName =
    comment?.creatorName ||
    (comment as any)?.creator_name ||
    getAuthorNameFromUser(
      (comment as any)?.author,
      (comment as any)?.authorId ||
        (comment as any)?.author_id ||
        (comment as any)?.createdBy ||
        (comment as any)?.created_by
    )

  const authorUser =
    (comment as any)?.author ||
    findUserById(
      (comment as any)?.authorId ||
        (comment as any)?.author_id ||
        (comment as any)?.createdBy ||
        (comment as any)?.created_by
    )

  const authorOrg = authorUser ? getOrgString(authorUser) : ''
  const formattedCommentDate =
    comment.createdAt && dayjs(comment.createdAt).isValid()
      ? dayjs(comment.createdAt).format('YYYY-MM-DD HH:mm')
      : comment.createdAt || ''

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        width="30vw"
        mask={false}
        centered={false}
        wrapClassName="pointer-events-none"
        style={{
          position: 'absolute',
          right: 60,
          top: 65,
          margin: 0,
          minWidth: 550,
          paddingBottom: 0,
          pointerEvents: 'auto'
        }}
        rootClassName="shared-permission-modal"
      >
        <div
          className="flex flex-col w-full bg-white rounded-lg"
          style={{ pointerEvents: 'auto', height: 'calc(86vh - 35px)' }}
        >
          {/* Header Title Section (Fixed) */}
          <div className="flex items-center justify-between mb-3 gap-4">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <Tooltip title={comment.title} placement="topLeft">
                <h2 className="text-[1.25rem] font-bold text-slate-800 mb-0 truncate max-w-[650px]">
                  {comment.title}
                </h2>
              </Tooltip>
            </div>

            <div className="flex items-center gap-2">
              <span
                className="flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-black text-white"
                style={{
                  backgroundColor:
                    CATEGORIES.find((c) => c.key === objectCat)?.colorHex ||
                    CATEGORIES[0]?.colorHex ||
                    '#2563EB',
                  fontFamily: 'Georgia, serif'
                }}
              >
                {objectCat}
              </span>

              <button
                type="button"
                className={clsx(
                  'h-8 px-3 text-xs font-bold rounded-full border-0 transition-colors flex items-center justify-center',
                  !onEdit ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer',
                  status === 'open'
                    ? clsx(
                        'bg-rose-50 text-rose-700',
                        onEdit && 'hover:bg-rose-100 active:bg-rose-200'
                      )
                    : clsx(
                        'bg-emerald-50 text-emerald-700',
                        onEdit && 'hover:bg-emerald-100 active:bg-emerald-200'
                      )
                )}
                onClick={() => {
                  if (!onEdit) return
                  setStatus((prev) => (prev === 'open' ? 'resolved' : 'open'))
                }}
              >
                {status === 'open' ? '● 未解決' : '✓ 解決済'}
              </button>

              <button
                type="button"
                className="h-8 px-3 border-0 rounded-full text-xs font-bold cursor-pointer transition-colors flex items-center justify-center hover:opacity-80 active:opacity-70"
                style={{
                  backgroundColor: `${getDirectoryPrimaryColor()}15`,
                  color: getDirectoryPrimaryColor()
                }}
                onClick={() => setShowRecipients((prev) => !prev)}
              >
                {recipientDisplay.buttonText}
              </button>
            </div>
          </div>

          {/* Status Change Warning Banner (Fixed) */}
          {Boolean(onEdit) && status !== comment.status && (
            <div className="mb-3 rounded bg-amber-50 px-3.5 py-2 text-[12px] font-semibold text-amber-700 ring-1 ring-amber-200">
              ※
              ステータスが変更されました（「保存」ボタンを押すことで確定します）
            </div>
          )}

          {/* Combined Scrollable Body Container (Original Post + Replies Thread) */}
          <div className="relative flex-1 min-h-[80px] flex flex-col bg-slate-50/50 rounded-xl border border-solid border-slate-200/80 mb-3 overflow-hidden">
            {/* Loading Overlay inside the reply display box frame */}
            {(isRefreshing || (isLoading && !isLoadingMore)) && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/60 backdrop-blur-[1px] select-none transition-all">
                <Spin size="default" />
                <span className="text-[12px] font-medium text-slate-500">
                  再読み込み中...
                </span>
              </div>
            )}

            <div
              ref={repliesContainerRef}
              className="flex-1 overflow-y-auto overflow-x-hidden p-3.5 space-y-3.5"
              onScroll={handleContainerScroll}
            >
              {/* Original Post Message Box */}
              <div className="rounded-xl border border-solid border-slate-200/90 bg-white p-3.5 shadow-xs">
                <div className="mb-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gradient-to-br from-slate-700 to-slate-800 text-[11px] font-bold text-white shadow-xs select-none">
                      {getInitials(authorName)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-bold text-slate-800">
                        {authorName}
                      </span>
                      {authorOrg && authorOrg !== '-' && (
                        <Tag className="rounded px-1.5 py-0 text-[10.5px] font-normal border-slate-200 bg-slate-50 text-slate-600 m-0">
                          {authorOrg}
                        </Tag>
                      )}
                    </div>
                  </div>

                  {formattedCommentDate && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-calendar flex-none text-slate-400"
                      >
                        <path d="M8 2v4" />
                        <path d="M16 2v4" />
                        <rect width="18" height="18" x="3" y="4" rx="2" />
                        <path d="M3 10h18" />
                      </svg>
                      <span>{formattedCommentDate}</span>
                    </div>
                  )}
                </div>

                {commentBodyStatus === 'loading' && (
                  <div className="py-2 pl-9 text-[12px] text-slate-400 flex items-center gap-2">
                    <Spin size="small" />
                    <span>コメント内容を読み込み中...</span>
                  </div>
                )}
                {commentBodyStatus === 'error' && (
                  <div className="pl-9">
                    <Tag color="error" className="my-1 text-[12px]">
                      ● コメントの取得に失敗しました
                    </Tag>
                  </div>
                )}
                {commentBodyStatus === 'success' && (
                  <div
                    className="ql-editor comment-body-display pl-9 pr-2 whitespace-pre-wrap text-[13px] text-slate-700 leading-relaxed break-words !p-0 !min-h-0 !h-auto !overflow-visible"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeCommentHtml(resolvedCommentBody || '')
                    }}
                  />
                )}
              </div>

              {/* Section Header: 返信 */}
              <div className="flex items-center justify-between pt-1 px-0.5">
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-message-square text-slate-500"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span className="text-[12.5px] font-bold text-slate-700">
                    返信
                  </span>
                  <span className="flex items-center justify-center rounded-full bg-slate-200/80 px-2 py-0.2 text-[10.5px] font-bold text-slate-600">
                    {activeReplies?.length || 0}
                  </span>
                </div>
                {hasMoreOlder && (
                  <span className="text-[11px] text-slate-400">
                    上方向にスクロールして過去ログ取得
                  </span>
                )}
              </div>

              {/* Threaded Replies Items */}
              {isLoadingMore && (
                <div className="py-2 text-center text-[12px] text-slate-400">
                  <Spin size="small" className="mr-2" />
                  過去の返信を読み込み中...
                </div>
              )}
              {activeReplies && activeReplies.length > 0 ? (
                <div className="space-y-2.5">
                  {[...activeReplies].reverse().map((r: any) => {
                    const replyAuthorName = getAuthorNameFromUser(
                      r.author,
                      r.authorId || r.author_id
                    )

                    const replyAuthorId =
                      r.authorId || r.author_id || r.author?.id
                    const replyUser = r.author || findUserById(replyAuthorId)
                    const replyAuthorOrg = replyUser
                      ? getOrgString(replyUser)
                      : ''

                    const replyDateRaw = r.createdAt || r.created_at || ''
                    const replyFormattedDate =
                      replyDateRaw && dayjs(replyDateRaw).isValid()
                        ? dayjs(replyDateRaw).format('YYYY-MM-DD HH:mm')
                        : replyDateRaw

                    const currentUserId = profile?.id
                    const isOwnReply = Boolean(
                      currentUserId &&
                        replyAuthorId &&
                        String(replyAuthorId) === String(currentUserId)
                    )
                    const isEditingThisReply = editingReplyId === r.id

                    return (
                      <div
                        key={r.id}
                        className={clsx(
                          'rounded-lg border border-solid p-3 text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-all',
                          isEditingThisReply
                            ? 'border-amber-400 bg-amber-50/60 ring-2 ring-amber-200/70'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-slate-600 text-[10.5px] font-bold text-white shadow-xs select-none">
                              {getInitials(replyAuthorName)}
                            </div>
                            <span className="text-[12.5px] font-bold text-slate-800">
                              {replyAuthorName}
                            </span>
                            {replyAuthorOrg && replyAuthorOrg !== '-' && (
                              <Tag className="rounded px-1.5 py-0 text-[10px] font-normal border-slate-200 bg-slate-50 text-slate-600 m-0">
                                {replyAuthorOrg}
                              </Tag>
                            )}
                            {isEditingThisReply && (
                              <Tag
                                color="orange"
                                className="rounded px-1.5 py-0 text-[10px] font-bold m-0"
                              >
                                編集中
                              </Tag>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            {replyFormattedDate && (
                              <span className="flex items-center gap-1 text-[11px] text-slate-400">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="11"
                                  height="11"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-calendar flex-none text-slate-400"
                                >
                                  <path d="M8 2v4" />
                                  <path d="M16 2v4" />
                                  <rect
                                    width="18"
                                    height="18"
                                    x="3"
                                    y="4"
                                    rx="2"
                                  />
                                  <path d="M3 10h18" />
                                </svg>
                                {replyFormattedDate}
                              </span>
                            )}

                            {isOwnReply && (
                              <div className="flex items-center gap-1.5 ml-1">
                                <span className="text-slate-200">|</span>
                                <button
                                  type="button"
                                  onClick={() => handleStartEditReply(r)}
                                  className={clsx(
                                    'text-[11px] font-medium hover:underline border-0 bg-transparent cursor-pointer p-0',
                                    isEditingThisReply
                                      ? 'text-amber-700 font-bold'
                                      : 'text-sky-600 hover:text-sky-800'
                                  )}
                                >
                                  {isEditingThisReply ? '編集中' : '編集'}
                                </button>
                                <span className="text-slate-200">·</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPendingDeleteReplyId(r.id)
                                    setDeleteReplyConfirmOpen(true)
                                  }}
                                  className="text-[11px] font-medium text-rose-600 hover:text-rose-800 hover:underline border-0 bg-transparent cursor-pointer p-0"
                                >
                                  削除
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="pl-8 text-slate-700 text-[12.5px] leading-relaxed">
                          <ResolvedReplyBody
                            reply={r}
                            formatBodyForDisplay={formatBodyForDisplay}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="py-5 flex flex-col items-center justify-center text-slate-400 gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-message-square text-slate-300"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span className="text-[12.5px] font-medium text-slate-500">
                    返信はまだありません
                  </span>
                  <span className="text-[11px] text-slate-400">
                    下の入力欄から返信を投稿できます
                  </span>
                </div>
              )}
            </div>

            {/* Fixed at bottom of scroll frame, but still within the frame */}
            <div className="flex items-center justify-center px-3.5 py-2 flex-none">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing || isLoading || isLoadingMore}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1 text-[11.5px] font-medium rounded-md border border-solid border-slate-300 bg-white text-slate-700 shadow-sm transition-all select-none',
                  isRefreshing || isLoading || isLoadingMore
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-slate-50 hover:text-slate-900 hover:border-slate-400 active:bg-slate-100'
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={clsx(
                    'lucide lucide-rotate-cw flex-none text-slate-500',
                    (isRefreshing || isLoading) && 'animate-spin text-sky-600'
                  )}
                >
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                <span>再読み込み</span>
              </button>
            </div>
          </div>

          {/* Reply Rich Text Editor (Fixed Sticky Footer for Detail Modal - No Outer Border Cover) */}
          <div className="w-full mb-2">
            {editingReplyId &&
              (() => {
                const editingReplyObj = activeReplies?.find(
                  (r: any) => r.id === editingReplyId
                )
                const editingReplyAuthorName = editingReplyObj
                  ? getAuthorNameFromUser(
                      editingReplyObj.author,
                      editingReplyObj.authorId ||
                        (editingReplyObj as any).author_id
                    )
                  : ''
                const rawSource = replyText || ''
                const isPathOrUrl =
                  isS3Url(rawSource) ||
                  rawSource.startsWith('assets/') ||
                  rawSource.startsWith('v1/')
                const cleanText = isPathOrUrl
                  ? ''
                  : decodeHtmlEntities(
                      rawSource
                        .replace(/<img[\s\S]*?>/gi, ' ')
                        .replace(/<\/(p|div|h[1-6]|li|tr|br\s*\/?)>/gi, ' ')
                        .replace(/<[^>]*>?/g, '')
                    )
                      .replace(/\s+/g, ' ')
                      .trim()
                const hasImage = /<img[\s\S]*?>/i.test(rawSource)
                const previewText = cleanText || (hasImage ? '[画像]' : '')
                const bodySnippet =
                  previewText.length > 25
                    ? `${previewText.slice(0, 25)}...`
                    : previewText

                return (
                  <div className="mb-2.5 flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-900 border border-solid border-amber-200 shadow-xs">
                    <div className="flex items-center gap-2 truncate">
                      <span className="font-bold text-amber-700">
                        ● 返信を編集中
                      </span>
                      {editingReplyAuthorName && (
                        <span className="font-semibold text-slate-700">
                          ({editingReplyAuthorName})
                        </span>
                      )}
                      {bodySnippet && (
                        <span className="truncate text-slate-600 max-w-[320px]">
                          「{bodySnippet}」
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelEditReply}
                      className="ml-2 flex items-center gap-1 shrink-0 text-amber-700 hover:text-amber-900 hover:underline border-0 bg-transparent cursor-pointer font-bold text-[12px]"
                    >
                      <span>キャンセル</span>
                      <span className="text-sm font-bold">×</span>
                    </button>
                  </div>
                )
              })()}

            <div className="mb-1.5 px-0.5">
              <span className="text-[12px] font-semibold text-slate-700">
                {editingReplyId ? 'コメント返信の編集' : 'コメント返信'}
              </span>
            </div>

            <div ref={editorWrapperRef} className="relative">
              <QuillEditorV3
                ref={quillEditorRef}
                value={replyText}
                onChange={(content) => {
                  setReplyText(content)
                }}
                onKeyDown={handleQuillKeyDown}
                onMentionTrigger={(pos) => {
                  if (pos) {
                    setMentionPos({ left: pos.left, top: pos.top })
                    if (pos.query !== undefined) {
                      setMentionSearchQuery(pos.query)
                    }
                  }
                  setShowMentionMenu(true)
                }}
                onMentionClose={() => setShowMentionMenu(false)}
                placeholder="ご返信を入力してください（@でメンション、@all で全員、@グループ名 でグループ）"
                height={160}
              />

              {showMentionMenu && (
                <div
                  style={
                    mentionPos
                      ? {
                          top: mentionPos.top,
                          left: Math.min(Math.max(10, mentionPos.left), 220),
                          transform: 'translateY(calc(-100% - 5px))'
                        }
                      : { bottom: '100%', left: 8 }
                  }
                  className="absolute z-50 mb-1 w-64 max-h-56 overflow-y-auto rounded-md bg-white p-2 shadow-lg ring-1 ring-black/5 text-xs"
                >
                  <div className="mb-1 flex items-center justify-between px-1">
                    <span className="text-[11px] font-bold text-slate-500">
                      メンションを選択
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowMentionMenu(false)}
                      className="text-[11px] text-slate-400 hover:text-slate-600 bg-transparent border-0 cursor-pointer"
                    >
                      ✕
                    </button>
                  </div>
                  <Input
                    size="small"
                    value={mentionSearchQuery}
                    onChange={(e) => setMentionSearchQuery(e.target.value)}
                    placeholder="検索..."
                    className="mb-2"
                  />
                  <div className="space-y-1">
                    {mentionItems.map((item, index) => {
                      const isSelected = index === mentionSelectedIndex
                      let colorClasses = 'hover:bg-sky-50 text-sky-700'
                      if (item.type === 'all')
                        colorClasses = 'hover:bg-amber-50 text-amber-700'
                      if (item.type === 'group')
                        colorClasses = 'hover:bg-violet-50 text-violet-700'

                      return (
                        <button
                          key={item.id}
                          ref={(el) => {
                            itemRefs.current[index] = el
                          }}
                          type="button"
                          onClick={item.action}
                          className={clsx(
                            'block w-full rounded px-2 py-1 text-left font-semibold border-0 cursor-pointer text-xs transition-colors',
                            colorClasses,
                            isSelected &&
                              'bg-slate-200 ring-1 ring-slate-400 font-bold'
                          )}
                        >
                          {item.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Action Buttons (Fixed) matching ModalAddAnnotation */}
          <div className="flex w-full items-center pt-3 mt-1.5 justify-between">
            <div className="flex items-center gap-2">
              {onDelete && (
                <Button
                  danger
                  type="primary"
                  onClick={() => onDelete(comment.id)}
                  sizing="rn-comment-footer-btn h-[38px] text-[14px]"
                >
                  {t('common.titleDeleteAnnotation', '削除')}
                </Button>
              )}

              {onEdit && (
                <Button
                  sizing="rn-comment-footer-btn h-[38px] text-[14px]"
                  className="bg-amber-500 text-white hover:!bg-amber-600 border-0 font-bold"
                  onClick={onEdit}
                >
                  {t('common.edit', '編集')}
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <Button
                sizing="rn-comment-footer-btn h-[38px] text-[14px]"
                onClick={onCancel}
              >
                {t('common.close', '閉じる')}
              </Button>

              {Boolean(onEdit) && status !== comment.status && (
                <Button
                  type="primary"
                  sizing="rn-comment-footer-btn h-[38px] text-[14px]"
                  className="theme-bg-primary"
                  loading={isSubmitting}
                  onClick={handleSaveStatus}
                >
                  {t('common.save', '保存')}
                </Button>
              )}

              <Button
                type="primary"
                sizing="rn-comment-footer-btn h-[38px] text-[14px]"
                className="bg-[#fbc02d] !text-white hover:!bg-[#f9a825] hover:!text-white border-0 font-bold"
                loading={isSubmitting || isLocalSubmitting}
                disabled={
                  isQuillEmpty(replyText) || isSubmitting || isLocalSubmitting
                }
                onClick={handleSendReply}
              >
                {editingReplyId ? '更新する' : t('comment.reply', '返信')}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Reply Delete Confirmation Modal */}
      {deleteReplyConfirmOpen && (
        <Modal
          open={deleteReplyConfirmOpen}
          onCancel={() => {
            setDeleteReplyConfirmOpen(false)
            setPendingDeleteReplyId(null)
          }}
          width={440}
          isEnterable
          onEnter={handleConfirmDeleteReply}
        >
          <Title
            title={t('common.titleDeleteAnnotation', '削除')}
            bottomMargin="mb-8"
          />

          <div className="flex flex-col gap-10 text-center">
            <Typography className="text-[14px] leading-[21px]">
              {t('comment.deleteReplyConfirmation', 'この返信を削除しますか？')}
            </Typography>
            <div className="flex justify-center gap-10">
              <Button
                sizing="w-[140px] h-[50px]"
                onClick={() => {
                  setDeleteReplyConfirmOpen(false)
                  setPendingDeleteReplyId(null)
                }}
              >
                {t('building.buttonCancel', 'キャンセル')}
              </Button>

              <Button
                sizing="w-[140px] h-[50px]"
                className="bg-[#B80808]"
                onClick={handleConfirmDeleteReply}
              >
                {t('common.delete', '削除')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Recipients List Popup Modal */}
      {showRecipients && (
        <Modal
          open={showRecipients}
          onCancel={() => setShowRecipients(false)}
          width="650px"
          rootClassName="shared-permission-modal"
        >
          <Title className="mb-4" title={recipientDisplay.modalTitle} />
          <Table
            dataSource={(userList || [])
              .filter((u: any) =>
                recipients.includes(Number(u.id || u.user_id))
              )
              .map(UserEntity.from)}
            rowKey={(user: UserEntity) => user.id}
            pagination={false}
            scroll={{ y: 260 }}
            size="small"
            bordered
            columns={[
              {
                title: '名前',
                dataIndex: 'name',
                render: (_, user: UserEntity) => (
                  <span className="font-semibold text-slate-800">
                    {user.getFullName()}
                  </span>
                )
              },
              {
                title: '組織名',
                dataIndex: 'organization',
                render: (_, user: UserEntity) => getOrgString(user.raw)
              },
              {
                title: 'メールアドレス',
                dataIndex: 'email',
                render: (_, user: UserEntity) => user.email || '-'
              },
              {
                title: '権限',
                dataIndex: 'role',
                render: (_, user: UserEntity) => (
                  <Tag>{getRoleString(user.raw)}</Tag>
                )
              }
            ]}
          />
          <div className="flex justify-end mt-4 pt-3 border-0 border-t border-solid border-[#EEEFF2]">
            <Button
              sizing="w-[140px] h-[40px]"
              onClick={() => setShowRecipients(false)}
            >
              {t('common.close', '閉じる')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}
